/**
 * Datum-/Uhrzeit-Eingaben im 24-h-Format.
 *
 * Native <input type="time"> / "datetime-local" richten sich nach der
 * Browser-/OS-Sprache und zeigen dort AM/PM — das lässt sich per Attribut
 * nicht erzwingen. Deshalb:
 *
 *   TimeInput24        — Uhrzeit als Textfeld "HH:MM" (24 h). Nimmt auch
 *                        "930", "9" oder "9.30" an und ergänzt beim
 *                        Verlassen zu "09:30" / "09:00".
 *   DateTimeInput      — Datum + Uhrzeit in EINEM Feld (gemeinsamer Rahmen),
 *                        gespeichert in zwei Spalten (Termine, Phasen-
 *                        Bedingungen, Wiedervorlage).
 *   DateTimeLocalInput — dasselbe für Formulare, die einen
 *                        "YYYY-MM-DDTHH:MM"-String halten (Ersatz für
 *                        <input type="datetime-local">).
 *
 * Konvention (wie bisher bei der Wiedervorlage): 00:00 = ganztägig. Ein Wert
 * ohne Uhrzeit wird mit 00:00 angezeigt; `splitDateTime` liefert für 00:00
 * `time: null`, wenn `midnightIsAllDay` gesetzt ist.
 */
import React, { useEffect, useRef, useState } from 'react';
import { DateInput } from './DateInput';

/* ─── Hilfsfunktionen ─────────────────────────────────────────────────── */

/** "YYYY-MM-DD" + "HH:MM" → "YYYY-MM-DDTHH:MM" ("" ohne Datum). */
export function joinDateTime(date: string | null | undefined, time: string | null | undefined): string {
  const d = (date ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return '';
  const t = typeof time === 'string' && /^\d{2}:\d{2}/.test(time) ? time.slice(0, 5) : '00:00';
  return `${d}T${t}`;
}

/** "YYYY-MM-DDTHH:MM" → { date, time }; leer → beide null. */
export function splitDateTime(
  value: string,
  { midnightIsAllDay = true }: { midnightIsAllDay?: boolean } = {},
): { date: string | null; time: string | null } {
  if (!value) return { date: null, time: null };
  const date = value.slice(0, 10);
  const time = value.slice(11, 16) || null;
  return { date, time: midnightIsAllDay && time === '00:00' ? null : time };
}

/**
 * Freie Eingabe → "HH:MM" (24 h) oder null bei ungültiger Eingabe; "" → "".
 * Akzeptiert "14:30", "1430", "930", "9", "9.30", "9:5" (→ 09:05).
 */
export function normalizeTime24(input: string): string | null {
  const s = input.trim();
  if (s === '') return '';
  let h: number;
  let m: number;
  const sep = s.match(/^(\d{1,2})\s*[:.,h]\s*(\d{1,2})$/);
  if (sep) {
    h = Number(sep[1]);
    m = Number(sep[2]);
  } else if (/^\d{1,4}$/.test(s)) {
    if (s.length <= 2) { h = Number(s); m = 0; }
    else { h = Number(s.slice(0, s.length - 2)); m = Number(s.slice(-2)); }
  } else {
    return null;
  }
  if (h > 23 || m > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/* ─── TimeInput24 ─────────────────────────────────────────────────────── */

interface TimeInput24Props {
  value: string | null | undefined;
  /** Gültiger Wert "HH:MM" oder "" (geleert). Ungültige Eingaben werden verworfen. */
  onChange: (value: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  readOnly?: boolean;
  style?: React.CSSProperties;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export function TimeInput24({ value, onChange, ariaLabel, disabled, readOnly, style, onBlur }: TimeInput24Props) {
  const stored = typeof value === 'string' && /^\d{2}:\d{2}/.test(value) ? value.slice(0, 5) : '';
  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => { setDraft(null); }, [stored]);

  const commit = () => {
    if (draft === null) return;
    const norm = normalizeTime24(draft);
    setDraft(null);
    if (norm !== null && norm !== stored) onChange(norm);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      aria-label={ariaLabel}
      placeholder="--:--"
      maxLength={5}
      value={draft ?? stored}
      disabled={disabled}
      readOnly={readOnly}
      onChange={e => {
        const v = e.target.value;
        setDraft(v);
        // Vollständige Eingabe ("14:30") sofort übernehmen.
        if (/^\d{2}:\d{2}$/.test(v)) {
          const norm = normalizeTime24(v);
          if (norm !== null && norm !== stored) onChange(norm);
        }
      }}
      onBlur={e => { commit(); onBlur?.(e); }}
      onKeyDown={e => { if (e.key === 'Enter') commit(); }}
      style={{ fontVariantNumeric: 'tabular-nums', ...style }}
    />
  );
}

/* ─── DateTimeInput (Datum + Uhrzeit, zwei Spalten) ───────────────────── */

interface DateTimeInputProps {
  date: string | null | undefined;
  time: string | null | undefined;
  /** Neuer Wert "YYYY-MM-DDTHH:MM" ("" = geleert) — beim Verlassen des Feldes, nur bei Änderung. */
  onCommit: (value: string) => void;
  /** Label des Datumsteils; der Uhrzeitteil bekommt "<Label> – Uhrzeit". */
  ariaLabel: string;
  disabled?: boolean;
  readOnly?: boolean;
  style?: React.CSSProperties;
  /** true → jede Änderung sofort übernehmen (Formulare mit eigenem Speichern-Button). */
  commitOnChange?: boolean;
}

export function DateTimeInput({ date, time, onCommit, ariaLabel, disabled, readOnly, style, commitOnChange }: DateTimeInputProps) {
  const stored = joinDateTime(date, time);
  const [draft, setDraftState] = useState<string | null>(null);
  // Ref-Spiegel: Blur der Uhrzeit setzt den Entwurf und unmittelbar danach
  // feuert der Blur des Rahmens — der muss den NEUEN Entwurf schon sehen.
  const draftRef = useRef<string | null>(null);
  const setDraft = (v: string | null) => { draftRef.current = v; setDraftState(v); };
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // Neuer gespeicherter Stand (z. B. nach Reload) verwirft einen alten Entwurf.
  useEffect(() => { setDraft(null); }, [stored]);

  const current = draft ?? stored;
  const curDate = current.slice(0, 10);
  const curTime = current ? current.slice(11, 16) : '';

  const update = (next: string) => {
    if (commitOnChange) { if (next !== stored) onCommit(next); return; }
    setDraft(next);
  };
  const commit = () => {
    const d = draftRef.current;
    if (d === null || d === stored) { setDraft(null); return; }
    onCommit(d);
  };
  // Erst übernehmen, wenn der Fokus das GANZE Feld verlässt (nicht beim
  // Wechsel zwischen Datum und Uhrzeit).
  const onBlurWrap = (e: React.FocusEvent) => {
    if (commitOnChange) return;
    const next = e.relatedTarget as Node | null;
    if (next && wrapRef.current?.contains(next)) return;
    commit();
  };

  const { width, ...boxStyle } = style ?? {};
  const inner: React.CSSProperties = {
    border: 'none', outline: 'none', background: 'transparent', color: 'inherit',
    font: 'inherit', padding: 0, minWidth: 0,
  };

  return (
    <div
      ref={wrapRef}
      onBlur={onBlurWrap}
      onKeyDown={e => { if (!commitOnChange && e.key === 'Enter') commit(); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, boxSizing: 'border-box',
        width: width ?? '100%', ...boxStyle,
        opacity: disabled ? 0.6 : undefined,
      }}
    >
      <DateInput
        ariaLabel={ariaLabel}
        value={curDate}
        disabled={disabled}
        readOnly={readOnly}
        onChange={d => update(d ? `${d}T${curTime || '00:00'}` : '')}
        style={{ ...inner, flex: 1, opacity: undefined }}
      />
      <TimeInput24
        ariaLabel={`${ariaLabel} – Uhrzeit`}
        value={curTime}
        disabled={disabled || !curDate}
        readOnly={readOnly}
        onChange={tm => { if (curDate) update(`${curDate}T${tm || '00:00'}`); }}
        style={{ ...inner, width: 48, textAlign: 'center' }}
      />
    </div>
  );
}

/* ─── DateTimeLocalInput (Ersatz für type="datetime-local") ───────────── */

interface DateTimeLocalInputProps {
  /** "YYYY-MM-DDTHH:MM" oder "". */
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  style?: React.CSSProperties;
}

export function DateTimeLocalInput({ value, onChange, ariaLabel, disabled, style }: DateTimeLocalInputProps) {
  return (
    <DateTimeInput
      ariaLabel={ariaLabel}
      date={value ? value.slice(0, 10) : ''}
      time={value ? value.slice(11, 16) : ''}
      commitOnChange
      disabled={disabled}
      onCommit={onChange}
      style={style}
    />
  );
}
